import React, { useEffect, useState, useMemo } from 'react';
import { db } from '../firebase';
import { getDocs, collection, doc, getDoc } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { useSchool } from '../hooks/useSchool';

const NON_AWARD_VALUE_TYPES = new Set(['balance correction', 'store refund']);

const summarizeTickets = (tickets = [], yearlyOverride = 0) => {
  let awardedTickets = 0;
  let positiveBalanceAdjustments = 0;
  let netStudentBalance = 0;
  const byValue = new Map();
  const byStaff = new Map();

  tickets.forEach((ticket) => {
    const quantity = Number(ticket.quantity ?? 1);
    if (!Number.isFinite(quantity)) return;
    netStudentBalance += quantity;

    const valueType = String(ticket.valueType || 'Unspecified').trim() || 'Unspecified';
    const isAward = quantity > 0
      && !NON_AWARD_VALUE_TYPES.has(valueType.toLowerCase());
    if (!isAward) {
      if (quantity > 0) positiveBalanceAdjustments += quantity;
      return;
    }

    awardedTickets += quantity;
    byValue.set(valueType, (byValue.get(valueType) || 0) + quantity);

    const giver = ticket.givenBy || {};
    const staffId = String(giver.uid || giver.email || giver.name || 'unknown');
    const staff = byStaff.get(staffId) || {
      staffId,
      name: String(giver.name || giver.email || 'Unknown / Imported'),
      email: String(giver.email || ''),
      tickets: 0,
    };
    staff.tickets += quantity;
    byStaff.set(staffId, staff);
  });

  return {
    statistics: {
      awardedTickets,
      positiveBalanceAdjustments,
      yearlyOverride,
      totalTicketsAwarded: awardedTickets + yearlyOverride,
      netStudentBalance,
    },
    analytics: {
      byValue: [...byValue.entries()]
        .map(([valueType, ticketCount]) => ({valueType, tickets: ticketCount}))
        .sort((a, b) => b.tickets - a.tickets),
      byStaff: [...byStaff.values()].sort((a, b) => b.tickets - a.tickets),
    },
  };
};

const ArchiveViewer = () => {
  const schoolId = useSchool();

  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState('');
  const [archivedUsers, setArchivedUsers] = useState([]);
  const [selectedClass, setSelectedClass] = useState('All');
  const [totals, setTotals] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [archiveSummaries, setArchiveSummaries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // ✅ Load available archive years
  useEffect(() => {
    const fetchYears = async () => {
      if (!schoolId) return;

      const snapshot = await getDocs(
        collection(db, 'schools', schoolId, 'archives')
      );

      const completeArchives = snapshot.docs
        .filter((archiveDoc) => {
          const data = archiveDoc.data();
          return data.schemaVersion !== 2 || data.status === 'complete';
        });
      const yearList = completeArchives.map(doc => doc.id);
      setYears(yearList.sort().reverse());
      setArchiveSummaries(completeArchives.map((archiveDoc) => {
        const data = archiveDoc.data();
        const legacySummary = data.statistics
          ? null
          : summarizeTickets(
            data.tickets || [],
            Number(data.totals?.totalTicketsOverride || 0)
          );
        return {
          year: archiveDoc.id,
          statistics: data.statistics || legacySummary.statistics,
          counts: data.counts || {
            tickets: (data.tickets || []).length,
            users: (data.users || []).length,
          },
        };
      }).sort((a, b) => b.year.localeCompare(a.year)));
    };

    fetchYears();
  }, [schoolId]);

  // ✅ Load selected archive
  const handleYearSelect = async (year) => {
    setSelectedYear(year);
    setSelectedClass('All');
    setArchivedUsers([]);
    setTotals(null);
    setStatistics(null);
    setAnalytics(null);
    setError('');

    if (!schoolId || !year) return;

    setLoading(true);
    try {
      const archiveRef = doc(db, 'schools', schoolId, 'archives', year);
      const archiveSnap = await getDoc(archiveRef);

      if (!archiveSnap.exists()) return;

      const data = archiveSnap.data();
      let users = data.users || [];
      let balances = data.balances || [];
      let selectedStatistics = data.statistics || null;
      let selectedAnalytics = data.analytics || null;

      if (data.schemaVersion === 2) {
        const requests = [
          getDocs(collection(archiveRef, 'users')),
          getDocs(collection(archiveRef, 'balances')),
        ];
        if (!selectedStatistics || !selectedAnalytics) {
          requests.push(getDocs(collection(archiveRef, 'tickets')));
        }
        const [usersSnap, balancesSnap, ticketsSnap] = await Promise.all(requests);
        users = usersSnap.docs.map(userDoc => ({
          id: userDoc.id,
          ...userDoc.data(),
        }));
        balances = balancesSnap.docs.map(balanceDoc => ({
          id: balanceDoc.id,
          ...balanceDoc.data(),
        }));
        if (ticketsSnap) {
          const summary = summarizeTickets(
            ticketsSnap.docs.map(ticketDoc => ticketDoc.data()),
            Number(data.totals?.totalTicketsOverride || 0)
          );
          selectedStatistics = summary.statistics;
          selectedAnalytics = summary.analytics;
        }
      } else if (!selectedStatistics || !selectedAnalytics) {
        const summary = summarizeTickets(
          data.tickets || [],
          Number(data.totals?.totalTicketsOverride || 0)
        );
        selectedStatistics = summary.statistics;
        selectedAnalytics = summary.analytics;
      }

      // 🔹 Build balances map
      const balancesMap = {};
      balances.forEach(b => {
        balancesMap[b.id] = b.balance;
      });

      // 🔹 Merge users + balances
      const usersWithBalances = users.map(u => ({
        ...u,
        ticketBalance: balancesMap[u.id] ?? 0,
      }));

      setArchivedUsers(usersWithBalances);
      setTotals(data.totals || null);
      setStatistics(selectedStatistics);
      setAnalytics(selectedAnalytics);
    } catch (loadError) {
      console.error(loadError);
      setError('Unable to load this archive.');
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Helper
  const getUserClass = (u) => u.class ?? u.advisory ?? 'Unassigned';

  // 🔹 Unique classes
  const uniqueClasses = useMemo(() => {
    return ['All', ...Array.from(new Set(archivedUsers.map(getUserClass)))];
  }, [archivedUsers]);

  // 🔹 Filter users
  const filteredUsers = useMemo(() => {
    if (selectedClass === 'All') return archivedUsers;
    return archivedUsers.filter(u => getUserClass(u) === selectedClass);
  }, [archivedUsers, selectedClass]);

  // 🔹 CSV export
  const exportToCSV = () => {
    const headers = ['Name', 'Class', 'Balance'];
    const rows = filteredUsers.map(u => [
      u.name ?? '',
      getUserClass(u),
      u.ticketBalance ?? 0
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.setAttribute('download', `archived-${selectedYear}.csv`);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div style={{ padding: '2rem' }}>
      <h1>📚 Archived Data</h1>

      {archiveSummaries.length > 0 && (
        <section style={sectionStyle}>
          <h2 style={sectionHeadingStyle}>Year-over-Year Summary</h2>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>School Year</th>
                <th style={thStyle}>Tickets Awarded</th>
                <th style={thStyle}>Ending Balance</th>
                <th style={thStyle}>Ledger Records</th>
              </tr>
            </thead>
            <tbody>
              {archiveSummaries.map(summary => (
                <tr key={summary.year}>
                  <td style={tdStyle}>{summary.year}</td>
                  <td style={tdStyle}>{summary.statistics?.totalTicketsAwarded ?? '—'}</td>
                  <td style={tdStyle}>{summary.statistics?.netStudentBalance ?? '—'}</td>
                  <td style={tdStyle}>{summary.counts?.tickets ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {/* Year Selector */}
      <div style={{ marginBottom: '1rem' }}>
        <label>Select Year: </label>
        <select
          value={selectedYear}
          onChange={(e) => handleYearSelect(e.target.value)}
        >
          <option value="">-- Select --</option>
          {years.map((year) => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>
      </div>

      {/* Year Info */}
      {selectedYear && (
        <p style={{ marginBottom: '1rem', color: '#475569' }}>
          Viewing archive for <strong>{selectedYear}</strong>
        </p>
      )}

      {loading && <p>Loading archive…</p>}
      {error && <p style={{ color: '#b91c1c' }}>{error}</p>}

      {/* Totals */}
      {statistics && (
        <div style={sectionStyle}>
          <div><strong>Tickets Awarded:</strong> {statistics.totalTicketsAwarded ?? 0}</div>
          <div><strong>Ending Student Balance:</strong> {statistics.netStudentBalance ?? 0}</div>
          {(statistics.yearlyOverride ?? 0) !== 0 && (
            <div>
              <strong>Yearly Total Adjustment:</strong>{' '}
              {statistics.yearlyOverride > 0 ? '+' : ''}{statistics.yearlyOverride}
            </div>
          )}
          {(statistics.positiveBalanceAdjustments ?? 0) > 0 && (
            <div>
              <strong>Positive Balance Corrections:</strong>{' '}
              {statistics.positiveBalanceAdjustments}
              {' '}(excluded from tickets awarded)
            </div>
          )}
        </div>
      )}
      {!statistics && totals && (
        <div style={{ marginBottom: '1rem' }}>
          <strong>Total Tickets:</strong> {totals.totalTicketsOverride ?? 0}
        </div>
      )}

      {analytics?.byValue?.length > 0 && (
        <section style={sectionStyle}>
          <h2 style={sectionHeadingStyle}>Tickets by School Value</h2>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Value</th>
                <th style={thStyle}>Tickets Awarded</th>
              </tr>
            </thead>
            <tbody>
              {analytics.byValue.map(item => (
                <tr key={item.valueType}>
                  <td style={tdStyle}>{item.valueType}</td>
                  <td style={tdStyle}>{item.tickets}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {analytics?.byStaff?.length > 0 && (
        <section style={sectionStyle}>
          <h2 style={sectionHeadingStyle}>Tickets Awarded by Staff</h2>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Staff Member</th>
                <th style={thStyle}>Tickets Awarded</th>
              </tr>
            </thead>
            <tbody>
              {analytics.byStaff.map(item => (
                <tr key={item.staffId}>
                  <td style={tdStyle}>
                    {item.name}
                    {item.email && item.email !== item.name ? ` (${item.email})` : ''}
                  </td>
                  <td style={tdStyle}>{item.tickets}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {/* Class Filter */}
      {archivedUsers.length > 0 && (
        <div style={{ marginBottom: '1rem' }}>
          <label>Filter by Class: </label>
          <select
            value={selectedClass}
            onChange={(e) => setSelectedClass(e.target.value)}
          >
            {uniqueClasses.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
      )}

      {/* Table */}
      {filteredUsers.length > 0 && (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Name</th>
              <th style={thStyle}>Class</th>
              <th style={thStyle}>Balance</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(user => (
              <tr key={user.id}>
                <td style={tdStyle}>{user.name ?? ''}</td>
                <td style={tdStyle}>{getUserClass(user)}</td>
                <td style={tdStyle}>{user.ticketBalance ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Export */}
      {filteredUsers.length > 0 && (
        <div style={{ marginTop: '1rem' }}>
          <button onClick={exportToCSV} style={exportBtn}>
            ⬇ Export CSV
          </button>
        </div>
      )}

      {/* Back Button */}
      <div style={{ marginTop: '2rem' }}>
        <Link to={`/${schoolId}/admin-tools`}>
          <button style={backBtn}>
            ⬅ Back to Admin Tools
          </button>
        </Link>
      </div>
    </div>
  );
};

// styles
const thStyle = {
  borderBottom: '1px solid #ccc',
  textAlign: 'left',
  padding: '0.65rem',
};

const tdStyle = {
  borderBottom: '1px solid #e5e7eb',
  padding: '0.65rem',
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
};

const sectionStyle = {
  marginBottom: '1.5rem',
  backgroundColor: '#fff',
  padding: '1rem',
  borderRadius: '8px',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
};

const sectionHeadingStyle = {
  marginTop: 0,
  marginBottom: '0.75rem',
  fontSize: '1.2rem',
};

const exportBtn = {
  backgroundColor: '#10b981',
  color: '#fff',
  padding: '0.5rem 1rem',
  borderRadius: '6px',
  border: 'none',
};

const backBtn = {
  backgroundColor: '#2563eb',
  color: 'white',
  padding: '0.5rem 1rem',
  borderRadius: '6px',
};

export default ArchiveViewer;
